import FileUploader from "@component-func/FileUploader";
import LoadingSpinnerCenter from "@component-ui/LoadingSpinnerCenter";
import { v4 as uuidV4 } from "uuid";
import { useMeetingMediaQuery } from "@hooks/meeting/useMeetingMediaQuery";
import { FC, useEffect, useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import NavigatableTable, {
    NavigatableTableProps,
} from "@component-generic/NavigatableTable";
import { TableCell } from "@component-ui/utility/Table";
import TenantUserAvatar from "@component-func/TenantUserAvatar";
import { Button } from "@component-ui/utility/Button";
import { ChevronRight } from "lucide-react";
import { humanizeDate } from "@libs/duration";
import SoftDeleteMediaButton from "./SoftDeleteMediaButton";

const MediaTable: FC = () => {
    const { meetingId } = useParams();
    const {
        data: mediaList,
        isLoading,
        isError,
        isSuccess,
    } = useMeetingMediaQuery(meetingId);
    const posthog = usePostHog();

    useEffect(() => {
        posthog?.capture("media_table_page_navigated");
        posthog?.capture("$pageview", { pageName: "/meeting/medias" });
    }, [meetingId]);

    const tableProps = useMemo<NavigatableTableProps>(() => {
        return {
            back: "/meetings",
            caption: "List of media",
            fileUploader: <FileUploader meetingId={meetingId} />,
            heads: mediaList
                ? [
                      {
                          id: uuidV4(),
                          content: <div>Language</div>,
                      },
                      {
                          id: uuidV4(),
                          content: (
                              <div className="text-center">Uploaded by</div>
                          ),
                      },
                      {
                          id: uuidV4(),
                          content: <div className="w-48">Created at</div>,
                      },
                      {
                          id: uuidV4(),
                          content: <div className="w-12">Transcript</div>,
                      },
                      {
                          id: uuidV4(),
                          content: <div />,
                      },
                  ]
                : [],
            rows: mediaList
                ? mediaList.map((m) => ({
                      id: m.id,
                      content: (
                          <>
                              <TableCell>{m.language}</TableCell>
                              <TableCell className="flex justify-center items-center">
                                  <TenantUserAvatar tenantUser={m.tenantUser} />
                              </TableCell>
                              <TableCell>{humanizeDate(m.createdAt)}</TableCell>
                              <TableCell>
                                  <Link
                                      to={`${m.id}/transcript?key=${encodeURIComponent(m.key)}&type=${m.mimeType}`}
                                  >
                                      <Button size="icon" variant="ghost">
                                          <ChevronRight className="w-8 h-8" />
                                      </Button>
                                  </Link>
                              </TableCell>
                              <TableCell>
                                  <SoftDeleteMediaButton id={m.id} />
                              </TableCell>
                          </>
                      ),
                  }))
                : [],
            fallbackFooter:
                "Do not have shared media yet, you can upload media files and view it here",
        };
    }, [mediaList]);

    if (!meetingId) return <Navigate to="/" />;

    if (isLoading) return <LoadingSpinnerCenter />;

    if (!isSuccess || isError)
        return (
            <div className="h-full grid place-items-center ">
                <h1 className="text-4xl text-foreground/50">
                    Cannot get the media for this meeting, please try again
                </h1>
            </div>
        );

    return <NavigatableTable {...tableProps} />;
};

export default MediaTable;
